import React, { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// App version for cache busting
const APP_VERSION = "1.0.19";
const BUILD_TIMESTAMP = "20251217-030000";

// Version checker - forces reload if new version deployed
const checkForNewVersion = async () => {
  try {
    // Check if we just reloaded to avoid infinite loop
    const lastReloadVersion = sessionStorage.getItem('lastReloadVersion');
    const lastReloadTime = sessionStorage.getItem('lastReloadTime');
    const now = Date.now();
    
    // If reloaded within last 30 seconds for same version, skip check
    if (lastReloadVersion === APP_VERSION && lastReloadTime && (now - parseInt(lastReloadTime)) < 30000) {
      console.log('⏭️ Skipping version check - recently reloaded');
      return;
    }
    
    const response = await fetch('/version.json?_=' + Date.now(), {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    const data = await response.json();
    
    // Only reload if version actually changed (ignore forceReload flag after first reload)
    if (data.version !== APP_VERSION && data.version !== lastReloadVersion) {
      console.log('🔄 New version detected:', data.version, 'Current:', APP_VERSION);
      
      // Mark that we're about to reload
      sessionStorage.setItem('lastReloadVersion', data.version);
      sessionStorage.setItem('lastReloadTime', now.toString());
      
      // Show notification
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10B981;
        color: white;
        padding: 16px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 99999;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 14px;
      `;
      notification.innerHTML = '✨ নতুন আপডেট পাওয়া গেছে! পেজ রিফ্রেশ হচ্ছে...';
      document.body.appendChild(notification);
      
      // Clear all caches
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      
      // Force reload after 1.5 seconds
      setTimeout(() => {
        window.location.reload(true);
      }, 1500);
    } else {
      console.log('✅ Version up-to-date:', APP_VERSION);
    }
  } catch (error) {
    console.log('Version check failed (normal if offline):', error.message);
  }
};

// Context
const AuthContext = createContext();

// API setup - Use same origin if REACT_APP_BACKEND_URL is empty (for production)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Set up axios defaults with timeout and retry
axios.defaults.baseURL = API;
axios.defaults.timeout = 30000; // 30 seconds timeout

// Request interceptor
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Disable caching for all requests
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';
  return config;
});

// Response interceptor with retry logic
let isRefreshing = false;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Network error retry logic (for sleep mode recovery, etc.)
    if (!error.response && !originalRequest._networkRetry) {
      // Network error (no response from server)
      originalRequest._networkRetry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      
      // Retry up to 3 times with exponential backoff
      if (originalRequest._retryCount <= 3) {
        const delay = Math.min(1000 * Math.pow(2, originalRequest._retryCount - 1), 5000);
        console.log(`[NETWORK-RETRY] Attempt ${originalRequest._retryCount}/3, waiting ${delay}ms`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return axios(originalRequest);
      }
    }
    
    // Check if this is a public route - don't redirect to login for public routes
    const isPublicRoute = originalRequest.url?.includes('/public/') || 
                          window.location.pathname.includes('/test/') ||
                          window.location.pathname.includes('/result/') ||
                          window.location.pathname.includes('/exam/writing/public/');
    
    // Handle 401 errors (only for non-public routes)
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicRoute) {
      originalRequest._retry = true;
      
      // Check if we have a valid token
      const token = localStorage.getItem('token');
      
      if (!token || isRefreshing) {
        // No token or already refreshing, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      // Token exists but might be expired, clear and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      const updatedUser = response.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Immediately refresh user data from server
      refreshUser();
    }
    setLoading(false);
    
    // Check for new version on mount
    checkForNewVersion();
    
    // Check every 5 minutes for new version
    const versionCheckInterval = setInterval(checkForNewVersion, 5 * 60 * 1000);
    
    return () => clearInterval(versionCheckInterval);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const register = async (userData) => {
    try {
      await axios.post('/auth/register', userData);
      toast.success('Registration successful! Please login.');
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, register, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return children;
};

// Import components
import LoginPage from "./components/auth/LoginPage";
import RegisterPage from "./components/auth/RegisterPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import AdminDashboard from "./components/admin/AdminDashboard";
import TestPreview from "./components/admin/TestPreview";
import InstructorDashboard from "./components/instructor/InstructorDashboard";
import StudentDashboard from "./components/student/StudentDashboard";
import PreExamGate from "./components/student/PreExamGate";
import WritingTestEnvironment from "./components/student/WritingTestEnvironment";
import StudentResults from "./components/student/StudentResults";
import SubmissionMarkingPage from "./components/instructor/SubmissionMarkingPage";
import PublicTestPage from "./components/public/PublicTestPage";
import ResultViewPage from "./components/public/ResultViewPage";
import initDevToolsPrevention from "./utils/devtoolsPrevention";

function App() {
  // Initialize DevTools prevention (disable only, no IP blocking)
  useEffect(() => {
    initDevToolsPrevention();
  }, []);

  return (
    <AuthProvider>
      <div className="App min-h-screen bg-slate-900">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/test/:publicLink" element={<PublicTestPage />} />
            <Route path="/result/:token" element={<ResultViewPage />} />
            <Route path="/exam/writing/public/:submissionId" element={<WritingTestEnvironment isGuest={true} />} />
            
            {/* Protected Routes */}
            <Route path="/admin/*" element={
              <ProtectedRoute allowedRoles={['admin', 'moderator']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/test-preview/:testId" element={
              <ProtectedRoute allowedRoles={['admin', 'instructor']}>
                <TestPreview />
              </ProtectedRoute>
            } />
            
            <Route path="/instructor/*" element={
              <ProtectedRoute allowedRoles={['instructor']}>
                <InstructorDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/student/*" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/exam/pre-gate/:testId" element={
              <ProtectedRoute allowedRoles={['student']}>
                <PreExamGate />
              </ProtectedRoute>
            } />
            
            <Route path="/exam/writing/:submissionId" element={
              <ProtectedRoute allowedRoles={['student']}>
                <WritingTestEnvironment />
              </ProtectedRoute>
            } />
            
            <Route path="/student/results" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentResults />
              </ProtectedRoute>
            } />
            
            <Route path="/instructor/mark/:submissionId" element={
              <ProtectedRoute allowedRoles={['instructor', 'admin', 'moderator']}>
                <SubmissionMarkingPage />
              </ProtectedRoute>
            } />
            
            {/* Default route */}
            <Route path="/" element={<DefaultRedirect />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

// Default redirect based on user role
const DefaultRedirect = () => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  switch (user.role) {
    case 'admin':
      return <Navigate to="/admin" replace />;
    case 'instructor':
      return <Navigate to="/instructor" replace />;
    case 'student':
      return <Navigate to="/student" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

export default App;